import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useImageMeta } from '@bingads-webui-campaign-react/smart-component-image';
import {
  useClientContext,
  useIsComponentSelected,
  useSiteConfig
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';
import { preventEventPropagation } from '@bingads-webui-campaign-react/smart-component-util';

import { useLocalImage } from './components/use-local-image';
import { ImageCropper } from './components/image-cropper';

const getRatio = (width, height) => height / width;
const getCssPercentStr = (width, height) => `${getRatio(width, height) * 100}%`;

const MaxWidth = 1080;
const MaxHeight = 608;
const MaxRatio = getRatio(MaxWidth, MaxHeight);
const MaxCssPercentStr = getCssPercentStr(MaxWidth, MaxHeight);

const getImageRatio = (width, height) => {
  if (!width || !height) {
    return MaxCssPercentStr;
  }

  if (getRatio(width, height) < MaxRatio) {
    return getCssPercentStr(width, height);
  }
  return MaxCssPercentStr;
};

const getStyles = components => ({
  container: {
    padding: '24px 10%',
  },
  mobileContainer: {
    ...components.section,
  },
  centerContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: MaxWidth,
  },
  sizedContainer: {
    position: 'relative',
  },
  content: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  selectedContent: {
    position: 'absolute',
    top: -100,
    left: 0,
    width: '100%',
    height: 'calc(100% + 200px)',
  },
});

const useClickOutsideHandler = (ref, cb) => {
  const handle = useCallback((e) => {
    if (ref.current && !e.target.classList.contains('image-cropper-preview-overlay') && !ref.current.contains(e.target)) { cb(); }
  }, [cb, ref]);

  useEffect(() => {
    document.addEventListener('click', handle);
    return () => document.removeEventListener('click', handle);
  }, [handle]);
};

const UnStyledSmartCoverImageViewWithCropper = ({
  imageId,
  imageName,
  imageWidth,
  imageHeight,
  cropSettings,
  id,
  i18n,
  classes,
  isEditMode,
  externalSrc,
}) => {
  const ref = useRef();
  const [isActive, setIsActive] = useState(false);

  const { isMobile } = useClientContext();
  const { ImageUrl } = useImageMeta({ imageId });
  const localImageSrc = useLocalImage(imageName);
  const src = (ImageUrl || externalSrc) || localImageSrc;
  const isExternalSrc = useMemo(() => !imageId && !!externalSrc, [imageId, externalSrc]);

  const selected = useIsComponentSelected('coverImage');

  const { dispatchSiteConfig } = useSiteConfig();

  const imageAspectRatio = imageHeight / imageWidth;
  const aspectRatiosProportion = imageAspectRatio / (MaxHeight / MaxWidth);
  // Only when user click "Cover Image" to expand the cover image editor,
  // can we show crop option. This is to fix a11y nested-interactive issue.
  const showEditMode = isEditMode && selected && !isMobile && aspectRatiosProportion > 1;

  const storeNewCropSettings = useCallback(
    (newCropSettings) => {
      dispatchSiteConfig({
        type: 'UPDATE_COVER_IMAGE',
        payload: {
          cropSettings: newCropSettings,
          editWithCropper: true,
        },
      });
    },
    [dispatchSiteConfig]
  );

  const setInactive = useCallback(() => setIsActive(false), []);

  const setInactiveOnClickOutside = useCallback(
    () => isActive && setInactive(),
    [isActive, setInactive]
  );

  useClickOutsideHandler(ref, setInactiveOnClickOutside);

  const contentStyle = selected && isActive
    ? classes.selectedContent
    : classes.content;

  const centerContainerProps = useMemo(
    () => (selected && isActive
      ? ({
        role: 'button',
        tabIndex: '0',
        onClick: preventEventPropagation,
        onKeyPress: preventEventPropagation,
      })
      : ({})),
    [isActive, selected]
  );

  return (
    <div
      id={id}
      className={classNames('cropper-inactivate-area', isMobile ? classes.mobileContainer : classes.container)}
    >
      <div
        ref={ref}
        className={classes.centerContainer}
        {...centerContainerProps}
      >
        <div
          className={classes.sizedContainer}
          style={{ paddingTop: getImageRatio(imageWidth, imageHeight) }}
        >
          <div className={classNames('content', contentStyle)}>
            {
              src &&
              <ImageCropper
                i18n={i18n}
                src={src}
                isExternalSrc={isExternalSrc}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                cropSettings={cropSettings}
                isEditMode={showEditMode}
                isActive={selected && isActive}
                setIsActive={setIsActive}
                onCropSettingsChange={storeNewCropSettings}
              />
            }
          </div>
        </div>
      </div>
    </div>
  );
};

UnStyledSmartCoverImageViewWithCropper.propTypes = {
  id: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  cropSettings: PropTypes.shape({}),

  // below two are exclusive, either by Id or Name
  imageId: PropTypes.string,
  imageName: PropTypes.string,
  isEditMode: PropTypes.bool,
  externalSrc: PropTypes.string,
};


UnStyledSmartCoverImageViewWithCropper.defaultProps = {
  imageId: null,
  imageName: null,
  imageWidth: null,
  imageHeight: null,
  cropSettings: null,
  isEditMode: false,
  externalSrc: null,
};

export const SmartCoverImageViewWithCropper = withErrorView(withDefaultStyles(
  UnStyledSmartCoverImageViewWithCropper,
  getStyles
), 'componentMode');
